// Shared "dial then go to the call screen" behavior, used by every place a
// call can be started (Main favorites/recents, contacts list, contact detail).
//
// dial() returning false is never left silent: a published key-change interlock
// lives on the call screen, so go there to show it; anything else gets an inline
// notice instead of a dead button (prime directive: never a dead end).

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLang, translate } from '../../i18n/index.js';
import { useScreens } from '../ScreensContext.js';

export interface Dialer {
  /** Inline notice to render near the call affordance, or null. */
  notice: string | null;
  setNotice: (value: string | null) => void;
  /** Start a call; `offlineHint` pre-seeds an "…is offline" notice. */
  dial: (peerUserId: string, offlineHint?: string) => Promise<void>;
}

export function useDialer(): Dialer {
  const { engine } = useScreens();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<string | null>(null);

  const dial = useCallback(
    async (peerUserId: string, offlineHint?: string): Promise<void> => {
      setNotice(offlineHint ?? null);
      let started = false;
      try {
        started = await engine.dial(peerUserId);
      } catch {
        setNotice(translate(getLang(), 'dialer.couldNotStart'));
        return;
      }
      if (started || engine.state.get().keyChange) navigate('/call');
      else if (!engine.state.get().call) {
        setNotice(translate(getLang(), 'dialer.couldNotStart'));
      }
    },
    [engine, navigate],
  );

  return { notice, setNotice, dial };
}
